import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

type Size = "sm" | "md" | "lg";

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  size = "md",
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  size?: Size;
  footer?: React.ReactNode;
}) {
  // монтируем, потом через rAF включаем enter-классы → плавная анимация
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- mount→rAF→enter необходимо для CSS-анимации входа
      setMounted(true);
      const r = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(r);
    } else if (mounted) {
      setEntered(false);
      const t = setTimeout(() => setMounted(false), 180);
      return () => clearTimeout(t);
    }
  }, [open, mounted]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!mounted) return null;

  const widthClass = size === "lg" ? "max-w-4xl" : size === "sm" ? "max-w-md" : "max-w-2xl";

  return createPortal(
    <div className="fixed inset-0 z-50 grid place-items-center p-3 sm:p-4">
      <div
        onClick={onClose}
        className={
          "absolute inset-0 bg-slate-900/35 backdrop-blur-[2px] transition-opacity duration-200 " +
          (entered ? "opacity-100" : "opacity-0")
        }
      />
      <div
        role="dialog"
        aria-modal="true"
        className={
          `relative w-full ${widthClass} overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[0_24px_60px_-12px_rgba(15,23,42,0.25)] transition-all duration-200 ` +
          (entered ? "translate-y-0 scale-100 opacity-100" : "translate-y-3 scale-[0.98] opacity-0")
        }
      >
        <header className="flex items-start justify-between gap-4 border-b border-[var(--color-border)] px-4 py-4 sm:px-5">
          <div className="min-w-0">
            <h2 className="truncate text-[16px] font-semibold tracking-[-0.01em] text-[var(--color-fg)]">
              {title}
            </h2>
            {subtitle && (
              <div className="mt-0.5 text-[12.5px] text-[var(--color-fg-muted)]">{subtitle}</div>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-[var(--color-fg-muted)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-fg)]"
            aria-label="Закрыть"
          >
            <X size={16} />
          </button>
        </header>
        <div className="max-h-[70vh] overflow-y-auto px-4 py-4 sm:px-5">{children}</div>
        {footer && (
          <footer className="flex flex-wrap justify-end gap-2 border-t border-[var(--color-border)] bg-[var(--color-surface-2)] px-4 py-3 sm:px-5">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
